import React from 'react'
import PrefillEditFieldsEditor from './PrefillEditFieldsEditor'
import { defaultColumnHeaders, getTableDimensions, resizeHeaders } from './tableFieldUtils'
import { supportsRecipientNameToggle } from './formFieldTypes'
import {
  PrefillEditFieldDefinition,
  getTableColumnPrefillEditFields,
  resizeTableColumnPrefillSettings,
  resizeTableRowPrefillSettings,
  supportsPrefillSettings
} from './prefillFieldSettingsUtils'

interface Props {
  isPrefillForm: boolean
  selectedElement: any
  selectedField: number
  currentPageIndex: number
  setFormElements: React.Dispatch<React.SetStateAction<any[][]>>
  selectedTableColumnIndex?: number | null
  onSelectTableColumn?: (columnIndex: number | null) => void
}

type PrefillEditOption = 'locked' | 'always' | 'conditional'

const getPrefillEditOption = (element: any): PrefillEditOption => {
  if (element.prefillEditable === false) return 'locked'
  if (element.prefillEditMode === 'editFields' || element.prefillEditMode === 'conditional') {
    return 'conditional'
  }
  return 'always'
}

const PrefillFieldSettings: React.FC<Props> = ({
  isPrefillForm,
  selectedElement,
  selectedField,
  currentPageIndex,
  setFormElements,
  selectedTableColumnIndex = null,
  onSelectTableColumn
}) => {
  if (!isPrefillForm || !supportsPrefillSettings(selectedElement?.type)) {
    return null
  }

  const updateSelectedElement = (updates: Record<string, unknown>) => {
    setFormElements((prev) => {
      const updated = [...prev]
      const currentPage = [...updated[currentPageIndex]]
      currentPage[selectedField] = {
        ...currentPage[selectedField],
        ...updates
      }
      updated[currentPageIndex] = currentPage
      return updated
    })
  }

  const editOption = getPrefillEditOption(selectedElement)
  const isTable = selectedElement.type === 'table'
  const showRecipientNameToggle = supportsRecipientNameToggle(selectedElement)
  const useAsRecipientName = selectedElement.useAsRecipientName === true
  const { cols, rows } = isTable ? getTableDimensions(selectedElement) : { cols: 0, rows: 0 }
  const columnHeaders = isTable
    ? resizeHeaders(selectedElement.columnHeaders, cols, defaultColumnHeaders)
    : []
  const columnSettings = isTable
    ? resizeTableColumnPrefillSettings(selectedElement.tableColumnPrefillSettings, cols)
    : []

  const selectedColumnIndex =
    selectedTableColumnIndex !== null &&
    selectedTableColumnIndex !== undefined &&
    selectedTableColumnIndex >= 0 &&
    selectedTableColumnIndex < cols
      ? selectedTableColumnIndex
      : null

  const selectedColumnEditFields =
    selectedColumnIndex !== null
      ? getTableColumnPrefillEditFields(selectedElement, selectedColumnIndex)
      : []

  const editFields: PrefillEditFieldDefinition[] = isTable
    ? selectedColumnEditFields
    : selectedElement.prefillEditFields || []

  const clearConditionalPrefillState = (): Record<string, unknown> => {
    const cleared: Record<string, unknown> = {
      prefillEditFields: [],
      prefillEditableOnCondition: null
    }
    if (isTable) {
      cleared.tableColumnPrefillSettings = resizeTableColumnPrefillSettings([], cols)
      cleared.tableRowPrefillSettings = resizeTableRowPrefillSettings([], rows)
    }
    return cleared
  }

  const handleEditOptionChange = (option: PrefillEditOption) => {
    if (option === 'locked') {
      updateSelectedElement({
        prefillEditable: false,
        prefillEditMode: 'always',
        ...clearConditionalPrefillState()
      })
      onSelectTableColumn?.(null)
      return
    }

    if (option === 'always') {
      updateSelectedElement({
        prefillEditable: true,
        prefillEditMode: 'always',
        ...clearConditionalPrefillState()
      })
      onSelectTableColumn?.(null)
      return
    }
    //

    const updates: Record<string, unknown> = {
      prefillEditable: true,
      prefillEditMode: 'editFields',
      prefillEditableOnCondition: null
    }

    if (isTable) {
      updates.tableColumnPrefillSettings = columnSettings
      updates.tableRowPrefillSettings = resizeTableRowPrefillSettings(
        selectedElement.tableRowPrefillSettings,
        rows
      )
      updates.prefillEditFields = []
    }

    updateSelectedElement(updates)
  }

  const updateColumnEditFields = (
    columnIndex: number,
    nextEditFields: PrefillEditFieldDefinition[]
  ) => {
    const nextSettings = resizeTableColumnPrefillSettings(
      selectedElement.tableColumnPrefillSettings,
      cols
    )
    nextSettings[columnIndex] = { prefillEditFields: nextEditFields }
    updateSelectedElement({
      tableColumnPrefillSettings: nextSettings,
      prefillEditMode: 'editFields',
      prefillEditable: true,
      prefillEditableOnCondition: null
    })
  }

  const handleRecipientNameToggle = (enabled: boolean) => {
    setFormElements((prev) =>
      prev.map((page, pageIndex) => {
        if (!Array.isArray(page)) return page
        return page.map((field, fieldIndex) => {
          const isSelected = pageIndex === currentPageIndex && fieldIndex === selectedField
          if (!supportsRecipientNameToggle(field)) {
            return field.useAsRecipientName ? { ...field, useAsRecipientName: false } : field
          }
          if (isSelected) {
            return { ...field, useAsRecipientName: enabled }
          }
          if (enabled) {
            return { ...field, useAsRecipientName: false }
          }
          return field
        })
      })
    )
  }

  return (
    <div className='bg-info bg-opacity-10 border border-info rounded p-3 mt-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <h5 className='mb-0'>Prefill Settings</h5>
        <span className='badge bg-info'>Prefilled form</span>
      </div>

      <div className='mb-3'>
        <div className='form-check'>
          <input
            className='form-check-input'
            type='radio'
            name='prefillEditOption'
            id='prefillEditOptionLocked'
            checked={editOption === 'locked'}
            onChange={() => handleEditOptionChange('locked')}
          />
          <label className='form-check-label' htmlFor='prefillEditOptionLocked'>
            Don&apos;t allow edit
          </label>
          <div className='text-muted small ms-4'>Recipient cannot change the prefilled value.</div>
        </div>

        <div className='form-check mt-2'>
          <input
            className='form-check-input'
            type='radio'
            name='prefillEditOption'
            id='prefillEditOptionAlways'
            checked={editOption === 'always'}
            onChange={() => handleEditOptionChange('always')}
          />
          <label className='form-check-label' htmlFor='prefillEditOptionAlways'>
            Allow edit
          </label>
          <div className='text-muted small ms-4'>
            Recipient can edit the prefilled value using the edit button before submitting.
          </div>
        </div>

        <div className='form-check mt-2'>
          <input
            className='form-check-input'
            type='radio'
            name='prefillEditOption'
            id='prefillEditOptionConditional'
            checked={editOption === 'conditional'}
            onChange={() => handleEditOptionChange('conditional')}
          />
          <label className='form-check-label' htmlFor='prefillEditOptionConditional'>
            Allow conditional edit
          </label>
          <div className='text-muted small ms-4'>
            {isTable
              ? 'Click Allow edit on a row below — options open directly under that row. Mark each edit field as isRequired so it cannot be skipped.'
              : 'Additional fields appear when the recipient edits. Mark each field as isRequired so required ones cannot be skipped.'}
          </div>
        </div>
      </div>

      {showRecipientNameToggle && (
        <div className='border-top pt-3 mt-3'>
          <div className='form-check form-switch'>
            <input
              className='form-check-input'
              type='checkbox'
              id='useAsRecipientNameToggle'
              checked={useAsRecipientName}
              onChange={(event) => handleRecipientNameToggle(event.target.checked)}
            />
            <label className='form-check-label' htmlFor='useAsRecipientNameToggle'>
              Use value in email greeting
            </label>
          </div>
          <div className='text-muted small ms-1 mt-1'>
            When enabled, this field&apos;s prefilled value is used in emails as &quot;Dear
            [value]&quot; instead of &quot;Dear User&quot;.
          </div>
        </div>
      )}

      {editOption === 'conditional' && isTable && selectedColumnIndex !== null && (
        <div className='border border-warning border-2 rounded p-3 bg-warning bg-opacity-10'>
          <h6 className='text-warning-emphasis'>
            Column edit — {columnHeaders[selectedColumnIndex]}
          </h6>
          <p className='text-muted small mb-2'>
            Legacy column-level conditional fields. Prefer configuring rows instead.
          </p>
          <PrefillEditFieldsEditor
            parentFieldId={selectedElement.fieldId}
            editFields={editFields}
            columnIndex={selectedColumnIndex}
            onChange={(nextEditFields) =>
              updateColumnEditFields(selectedColumnIndex, nextEditFields)
            }
          />
        </div>
      )}

      {editOption === 'conditional' && !isTable && (
        <div className='border border-warning border-2 rounded p-3 bg-warning bg-opacity-10'>
          <h6 className='text-warning-emphasis'>Conditional edit fields</h6>
          <PrefillEditFieldsEditor
            parentFieldId={selectedElement.fieldId}
            editFields={editFields}
            onChange={(nextEditFields) =>
              updateSelectedElement({
                prefillEditFields: nextEditFields,
                prefillEditMode: 'editFields',
                prefillEditable: true,
                prefillEditableOnCondition: null
              })
            }
          />
        </div>
      )}
    </div>
  )
}

export default PrefillFieldSettings
